/**
 * V2 Flow shell panel container — pure layout math (no React, no DOM).
 *
 * Source of truth: `.baton/2026-09-30-terra-v2-panel-interactions.md` (the
 * interaction spec's state table) and
 * `plans/2026-09-30-005-v2-panel-container-plan.md`'s Key Technical
 * Decisions D1–D5. This file is the contract for Requirements Trace rules
 * R1, R3, R5, R6, R7, and R10:
 *
 *   R1  Three columns, fixed order Chat · Flow · Actions; widths are
 *       fractions of the row (`flex-grow` = share, `flex-basis: 0`),
 *       summing to 1. One open side panel = 0.25; both open = 0.25 each,
 *       Flow the remainder (spec table rows 1–4).
 *   R3  Hard floors: side panels 320px, Flow 480px. A drag that would
 *       violate any visible segment's floor pins at the floor; dragging can
 *       never close a column.
 *   R5  Flow actually collapses only while both side panels are open AND
 *       pushing (not overlay): `collapsed = flowClosed && chatOpen &&
 *       actionsOpen && mode === 'push'`, derived every render, never a
 *       stored bit. Closing either panel restores Flow in the same render.
 *   R6  Flow collapsed => exactly ONE divider, between Chat and Actions
 *       (spec table row 5: 0.5 / 0 / 0.5 by default).
 *   R7  `flowClosed` true in any other configuration leaves the Flow column
 *       in place at its normal width and swaps in the empty state.
 *   R10 Overlay mode below `(max-width: 1119px)`: an open side panel floats
 *       over Flow at a proportional quarter width (`%`, not px); Flow keeps
 *       full width.
 *
 * D1: shares (fractions in [0, 1]), not pixels, are the stored unit; pixels
 * exist only at the drag handle, converted at the boundary via `pxToShare`.
 * D4: `sideShareBounds`/`splitShareBounds` pin both bounds to the side-panel
 * floor share rather than returning an inverted ARIA range — an inverted
 * range (`aria-valuemin > aria-valuemax`) is invalid, and a frozen handle
 * beats it. That is a *bounds* rule only — layout under a degenerate row
 * scales every visible floor proportionally (`*_FLOORS_PX`), and
 * `FlowPanels` freezes each handle at its effective width.
 * D5: `pxToShare(px, 0)` returns `0`, never `NaN`/`Infinity`, so a
 * not-yet-measured row still renders valid grows.
 *
 * Stored shares are requests, not guarantees — derivation clamps them
 * against the CURRENT bounds, so a share dragged while the other panel was
 * closed (or before a window resize) can never push Flow under its floor or
 * below zero. The store is never written back; the raw share is restored
 * when the context that made it valid returns (same model as the collapsed
 * split preserving the quarter shares). See `reconcileSideShares`. A
 * both-open drag also pins the other panel to its effective share
 * (`commitSideShare` in `FlowPanels`), so a stale request never outlives one
 * interaction. A degenerate row (`*_FLOORS_PX`) freezes every handle and
 * commits nothing.
 */

export const SIDE_PANEL_DEFAULT_SHARE = 0.25;
export const COLLAPSED_CHAT_DEFAULT_SHARE = 0.5;
export const SIDE_PANEL_MIN_PX = 320;
export const FLOW_MIN_PX = 480;
/** Width of each divider corridor (`DividerFrame` in FlowPanels.tsx reads this via inline style). Dividers have flex-grow 0, so the three columns share `rowWidth - DIVIDER_PX * dividers.length`, not the full row. */
export const DIVIDER_PX = 8;
/**
 * Sum of every visible column's floor per configuration. Below these the
 * row is DEGENERATE: `reconcileSideShares`/`deriveFlowPanelsLayout` give
 * every visible column `its floor / this sum` (stored shares ignored) so
 * the grows still sum to 1 and no column — Flow included — ever hits 0
 * unless it is explicitly closed. `OVERLAY_QUERY` is `BOTH_SIDES_FLOORS_PX - 1`
 * on the VIEWPORT; the container can be narrower (a dock or sidebar), so
 * push mode must survive these widths on its own.
 */
export const ONE_SIDE_FLOORS_PX = SIDE_PANEL_MIN_PX + FLOW_MIN_PX; // 800
export const BOTH_SIDES_FLOORS_PX = 2 * SIDE_PANEL_MIN_PX + FLOW_MIN_PX; // 1120
export const COLLAPSED_FLOORS_PX = 2 * SIDE_PANEL_MIN_PX; // 640
// 1119 = BOTH_SIDES_FLOORS_PX - 1: the push-mode row can't hold both floors
// plus Flow's floor below 1120px, so overlay kicks in one pixel below that
// threshold.
export const OVERLAY_QUERY = '(max-width: 1119px)';

export type FlowPanelId = 'chat' | 'actions';
export type FlowPanelsMode = 'push' | 'overlay';
export type FlowDividerId = 'chat-flow' | 'flow-actions' | 'chat-actions';

export interface FlowPanelsLayoutInput {
  chatOpen: boolean;
  actionsOpen: boolean;
  flowClosed: boolean;
  chatShare: number;
  actionsShare: number;
  collapsedChatShare: number;
  mode: FlowPanelsMode;
  /** Measured row `clientWidth`, gutters included; `0` = not yet measured. */
  rowWidth: number;
}

export interface FlowPanelsLayout {
  mode: FlowPanelsMode;
  collapsed: boolean;
  showEmptyState: boolean;
  /** The width the three flex columns actually share; every px<->share conversion in `FlowPanels` uses this, never `rowWidth`. */
  columnsWidth: number;
  /** True when `columnsWidth` can't hold every visible column's floor: grows are the floors scaled proportionally, stored shares are ignored, and `FlowPanels` freezes every handle at its effective width. Always false in overlay mode. */
  degenerate: boolean;
  grows: {
    chat: number;
    flow: number;
    actions: number;
  };
  dividers: FlowDividerId[];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Converts a pixel floor/measurement into a share of the row width.
 * `rowWidth <= 0` (not yet measured — see D5) returns `0`, never
 * `NaN`/`Infinity`.
 */
export function pxToShare(px: number, rowWidth: number): number {
  return rowWidth > 0 ? clamp(px / rowWidth, 0, 1) : 0;
}

/**
 * Drag bounds for a single side panel's inner-edge handle (D1, D3). `max`
 * reserves the other side panel's current share (if open) plus Flow's
 * floor; D4's degenerate rule pins both bounds to the side-panel floor
 * share when the row is too narrow for every floor to hold at once.
 */
export function sideShareBounds(args: {
  rowWidth: number;
  otherOpen: boolean;
  otherShare: number;
}): { minShare: number; maxShare: number } {
  const { rowWidth, otherOpen, otherShare } = args;
  const minShare = pxToShare(SIDE_PANEL_MIN_PX, rowWidth);
  const maxShare = 1 - (otherOpen ? otherShare : 0) - pxToShare(FLOW_MIN_PX, rowWidth);
  if (maxShare < minShare) {
    return { minShare, maxShare: minShare };
  }
  return { minShare, maxShare };
}

const NO_DIVIDERS: FlowDividerId[] = [];

/**
 * Reconciles a side panel's stored share against the CURRENT bounds. A
 * share is stored in whatever context it was dragged (the other panel's
 * open state, the row's width at the time) — this is the seam where a
 * stale request gets clamped back to something the current row can honor,
 * same rule a live drag already follows via `sideShareBounds`.
 *
 * Both-open rationale: the larger panel is the one that was stretched while
 * the other was closed (or the row was wider), so it yields first; shaving
 * down to equal, then equally, keeps a symmetric input from producing a
 * lopsided result.
 *
 * Degenerate rows (below `*_FLOORS_PX`): every visible column gets `floor /
 * sum of floors`. Pinning the side panels at their full floor instead would
 * make Flow absorb the entire deficit and hit 0 while `flowClosed` is false
 * — the same invariant a stale share used to break, via a different path.
 */
export function reconcileSideShares(args: {
  columnsWidth: number;
  chatOpen: boolean;
  actionsOpen: boolean;
  chatShare: number;
  actionsShare: number;
}): { chat: number; actions: number; degenerate: boolean } {
  const { columnsWidth, chatOpen, actionsOpen, chatShare, actionsShare } = args;

  if (!chatOpen && !actionsOpen) {
    return { chat: 0, actions: 0, degenerate: false };
  }

  if (chatOpen !== actionsOpen) {
    // Exactly one open.
    if (columnsWidth < ONE_SIDE_FLOORS_PX) {
      const side = SIDE_PANEL_MIN_PX / ONE_SIDE_FLOORS_PX; // 0.4 — Flow gets 0.6
      return chatOpen
        ? { chat: side, actions: 0, degenerate: true }
        : { chat: 0, actions: side, degenerate: true };
    }
    // columnsWidth >= 800 ⇒ maxShare >= minShare here, never the D4 pin.
    const bounds = sideShareBounds({ rowWidth: columnsWidth, otherOpen: false, otherShare: 0 });
    return chatOpen
      ? { chat: clamp(chatShare, bounds.minShare, bounds.maxShare), actions: 0, degenerate: false }
      : { chat: 0, actions: clamp(actionsShare, bounds.minShare, bounds.maxShare), degenerate: false };
  }

  // Both open.
  if (columnsWidth < BOTH_SIDES_FLOORS_PX) {
    const side = SIDE_PANEL_MIN_PX / BOTH_SIDES_FLOORS_PX; // 2/7 each — Flow gets 3/7
    return { chat: side, actions: side, degenerate: true };
  }
  const sideMin = pxToShare(SIDE_PANEL_MIN_PX, columnsWidth);
  const flowMin = pxToShare(FLOW_MIN_PX, columnsWidth);
  const available = 1 - flowMin; // columnsWidth >= 1120 ⇒ available >= 2 * sideMin

  const c = Math.max(chatShare, sideMin);
  const a = Math.max(actionsShare, sideMin);
  const excess = c + a - available;
  if (excess <= 0) {
    return { chat: c, actions: a, degenerate: false };
  }

  const hi = Math.max(c, a);
  const lo = Math.min(c, a);
  if (hi - excess >= lo) {
    // Shave only the larger one by the excess; a tie shaves chat.
    if (c >= a) {
      return { chat: c - excess, actions: a, degenerate: false };
    }
    return { chat: c, actions: a - excess, degenerate: false };
  }
  // Shaving the larger one alone would cross below the smaller — split the
  // whole available width evenly instead.
  const half = available / 2;
  return { chat: half, actions: half, degenerate: false };
}

/**
 * THE one collapse formula (R5/R9) — any reader of "is Flow collapsed"
 * outside `deriveFlowPanelsLayout` calls this function rather than
 * re-deriving the three-flag AND itself (today that is only the tests;
 * `rail/RailDock.tsx` used to read it through `useFlowPanelsCollapsed.ts`
 * to host the panel toggles, which now follow `derivePanelTogglesHost`
 * below instead — collapsed is one of its `'actions'` cases).
 * `deriveFlowPanelsLayout` below is the in-render caller; the two must
 * never disagree, which is why this is a standalone export instead of
 * inlined twice.
 *
 * Overlay mode never collapses (D6/R10): an open side panel floats over a
 * full-width Flow column instead of pushing it, so there is nothing to
 * collapse.
 */
export function isFlowCollapsed(
  input: Pick<FlowPanelsLayoutInput, 'flowClosed' | 'chatOpen' | 'actionsOpen' | 'mode'>,
): boolean {
  const { flowClosed, chatOpen, actionsOpen, mode } = input;
  return mode !== 'overlay' && flowClosed && chatOpen && actionsOpen;
}

/** Which header hosts the three mode toggles (`shell-next/PanelToggles.tsx`). */
export type PanelTogglesHost = 'flow' | 'actions';

/**
 * THE one "where do the toggles render" formula (Spencer QA round 3). The
 * toggles pin to the TOP-RIGHT CORNER OF THE WHOLE SHELL — the right edge
 * of whichever column paints rightmost — not to the Flow column's own edge:
 *  - `'actions'`: push mode with the Actions column open. The column is a
 *    flex sibling at the row's right edge, so its dock strip
 *    (`rail/RailDock.tsx`) hosts the toggles. Round 2's "collapsed" case
 *    (Chat + Actions open, `flowClosed`) is a special case of this —
 *    collapsed ⇒ Actions open ⇒ `'actions'` — pinned by a test.
 *  - `'flow'`: everything else. Actions closed ⇒ Flow is rightmost. Overlay
 *    mode ⇒ an open Actions panel FLOATS under the full-width Flow header
 *    (R10), so the Flow header's right edge is still the corner.
 * Read via `usePanelTogglesHost.ts` outside `FlowPanels`; `Header.tsx` and
 * `rail/RailDock.tsx` each render `PanelToggles` only when this names them,
 * so exactly one copy is visible per state.
 */
export function derivePanelTogglesHost(
  input: Pick<FlowPanelsLayoutInput, 'actionsOpen' | 'mode'>,
): PanelTogglesHost {
  return input.mode === 'push' && input.actionsOpen ? 'actions' : 'flow';
}

/**
 * Derives the whole row's layout from the panel open/closed/share state for
 * one render — D3: `collapsed` and `showEmptyState` are always computed,
 * never stored. Implements R1/R5/R6/R7/R10 (spec table rows 1-5 in push
 * mode; overlay per D6).
 */
export function deriveFlowPanelsLayout(input: FlowPanelsLayoutInput): FlowPanelsLayout {
  const {
    chatOpen,
    actionsOpen,
    flowClosed,
    chatShare,
    actionsShare,
    collapsedChatShare,
    mode,
    rowWidth,
  } = input;

  if (mode === 'overlay') {
    return {
      mode,
      collapsed: false,
      showEmptyState: flowClosed,
      columnsWidth: rowWidth,
      degenerate: false,
      grows: {
        chat: chatOpen ? chatShare : 0,
        flow: 1,
        actions: actionsOpen ? actionsShare : 0,
      },
      dividers: NO_DIVIDERS,
    };
  }

  const collapsed = isFlowCollapsed({ flowClosed, chatOpen, actionsOpen, mode });
  const showEmptyState = flowClosed && !collapsed;

  if (collapsed) {
    const dividers: FlowDividerId[] = ['chat-actions'];
    const columnsWidth = Math.max(0, rowWidth - DIVIDER_PX * dividers.length);
    const degenerate = columnsWidth < COLLAPSED_FLOORS_PX;
    const bounds = splitShareBounds({ rowWidth: columnsWidth });
    const chat = degenerate
      ? SIDE_PANEL_MIN_PX / COLLAPSED_FLOORS_PX // 0.5 / 0.5
      : clamp(collapsedChatShare, bounds.minShare, bounds.maxShare);
    return {
      mode,
      collapsed: true,
      showEmptyState: false,
      columnsWidth,
      degenerate,
      grows: {
        chat,
        flow: 0,
        actions: 1 - chat,
      },
      dividers,
    };
  }

  if (chatOpen && actionsOpen) {
    const dividers: FlowDividerId[] = ['chat-flow', 'flow-actions'];
    const columnsWidth = Math.max(0, rowWidth - DIVIDER_PX * dividers.length);
    const { chat, actions, degenerate } = reconcileSideShares({
      columnsWidth,
      chatOpen,
      actionsOpen,
      chatShare,
      actionsShare,
    });
    return {
      mode,
      collapsed: false,
      showEmptyState,
      columnsWidth,
      degenerate,
      grows: {
        chat,
        flow: 1 - chat - actions,
        actions,
      },
      dividers,
    };
  }

  if (chatOpen) {
    const dividers: FlowDividerId[] = ['chat-flow'];
    const columnsWidth = Math.max(0, rowWidth - DIVIDER_PX * dividers.length);
    const { chat, degenerate } = reconcileSideShares({
      columnsWidth,
      chatOpen,
      actionsOpen,
      chatShare,
      actionsShare,
    });
    return {
      mode,
      collapsed: false,
      showEmptyState,
      columnsWidth,
      degenerate,
      grows: { chat, flow: 1 - chat, actions: 0 },
      dividers,
    };
  }

  if (actionsOpen) {
    const dividers: FlowDividerId[] = ['flow-actions'];
    const columnsWidth = Math.max(0, rowWidth - DIVIDER_PX * dividers.length);
    const { actions, degenerate } = reconcileSideShares({
      columnsWidth,
      chatOpen,
      actionsOpen,
      chatShare,
      actionsShare,
    });
    return {
      mode,
      collapsed: false,
      showEmptyState,
      columnsWidth,
      degenerate,
      grows: { chat: 0, flow: 1 - actions, actions },
      dividers,
    };
  }

  return {
    mode,
    collapsed: false,
    showEmptyState,
    columnsWidth: Math.max(0, rowWidth),
    degenerate: false,
    grows: { chat: 0, flow: 1, actions: 0 },
    dividers: NO_DIVIDERS,
  };
}

/**
 * Drag bounds for the collapsed single divider between Chat and Actions
 * (R6). Both sides are bounded by the side-panel floor; D4's degenerate
 * rule pins both bounds to that floor when the row can't hold both floors.
 */
export function splitShareBounds(args: { rowWidth: number }): {
  minShare: number;
  maxShare: number;
} {
  const { rowWidth } = args;
  const minShare = pxToShare(SIDE_PANEL_MIN_PX, rowWidth);
  const maxShare = 1 - minShare;
  if (maxShare < minShare) {
    return { minShare, maxShare: minShare };
  }
  return { minShare, maxShare };
}
